import { ChevronRight, History } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'

import { useRecentHistory } from '@/api/hooks'
import { Button, Skeleton } from '@/components/ui'
import { ErrorNotice } from '@/components/ui/Feedback'

import { ClearHistoryDialog } from './ClearHistoryDialog'
import { HistoryRows } from './HistoryRows'
import { SettingsSection } from './SettingsSection'

/** Enough to recognise that something is being recorded, few enough that the
 *  settings below this card are still reachable without scrolling past a log. */
const PREVIEW_COUNT = 5

/**
 * The listening log: what it holds, and the button that empties it.
 *
 * This app ships an incognito mode, which is a promise that some listening is
 * not recorded. A promise like that is only worth something next to a plain
 * account of what *is* recorded when incognito is off — and a way to erase it.
 * That is what this section is for. It is deliberately in Settings next to
 * Incognito rather than hidden behind a menu.
 *
 * It shows the most recent handful and links to the rest. A settings page is a
 * list of controls, and a card that grows without limit turns everything under
 * it into something you have to scroll a history to reach.
 */
export function HistorySection() {
  const [confirming, setConfirming] = useState(false)
  const history = useRecentHistory(PREVIEW_COUNT)

  const events = history.data?.results ?? []
  const hasMore = Boolean(history.data?.next)

  return (
    <SettingsSection
      icon={History}
      title="Listening history"
      headerAction={
        events.length > 0 ? (
          <Button variant="secondary" size="sm" onClick={() => setConfirming(true)}>
            Clear
          </Button>
        ) : null
      }
    >
      <div className="rounded-card border border-border bg-surface-sunken">
        {history.isPending ? (
          <div className="flex flex-col gap-2 p-3">
            {[0, 1, 2].map((row) => (
              <Skeleton key={row} className="h-4 w-full" />
            ))}
          </div>
        ) : history.isError ? (
          <ErrorNotice busy={history.isFetching} onRetry={() => void history.refetch()}>
            Could not load your history.
          </ErrorNotice>
        ) : events.length === 0 ? (
          <p className="p-3 text-sm text-text-subtle">
            No listening history yet.
          </p>
        ) : (
          <>
            <HistoryRows events={events} />
            {hasMore ? (
              <Link
                to="/settings/history"
                className="flex min-h-11 items-center justify-between gap-2 border-t border-border px-3 text-sm font-medium text-accent transition-colors hover:bg-surface-hover"
              >
                See all
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            ) : null}
          </>
        )}
      </div>

      {confirming ? <ClearHistoryDialog onClose={() => setConfirming(false)} /> : null}
    </SettingsSection>
  )
}
